import { useState } from "react";
import get from "lodash.get";
import { exportToCSV, exportToExcel, exportToJSON } from "@/utils/export";
import { ListExportDropdown } from "@/types/list";

export type ExportFormat = "csv" | "excel" | "json";

const sanitizeFilename = (name: string): string =>
	name
		.toLowerCase()
		.trim()
		.replace(/[^a-z0-9_\-]+/gi, "_")
		.replace(/_+/g, "_")
		.replace(/^_+|_+$/g, "");

export const useExportTable = <TData>(props: ListExportDropdown<TData>) => {
	const { table, tableName, expandedRowPanelExport } = props;

	const [format, setFormat] = useState<ExportFormat>("csv");
	const [exportAll, setExportAll] = useState(false);
	const allData = table.options.meta?.allData as TData[] | undefined;

	const visibleColumns = table.getVisibleLeafColumns().filter(col => col.columnDef.meta?.enableExport !== false);

	const getFlatExportHeaders = () => {
		return visibleColumns.map(col => {
			const parts: string[] = [];
			let parent = col.parent;

			while (parent) {
				if (parent.columnDef?.header && typeof parent.columnDef.header === "string") {
					parts.unshift(parent.columnDef.header);
				}
				parent = parent.parent;
			}

			const header = typeof col.columnDef.header === "string" ? col.columnDef.header : col.id;
			parts.push(header);

			const unit = col.columnDef.meta?.unit;
			if (unit) parts[parts.length - 1] += ` (${unit})`;

			return parts.join(" ");
		});
	};

	const headers = getFlatExportHeaders();
	const filename =
		sanitizeFilename(tableName || "export") + "_" + new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");

	const getExportData = (): TData[] => {
		return table.getRowModel().rows.map(r => r.original);
	};

	const getExportRows = (): (string | number)[][] => {
		const allRows: (string | number)[][] = [];

		const rows = table.getRowModel().rows;

		rows.forEach(row => {
			const parentData = visibleColumns.map(col => {
				const columnKey = "accessorKey" in col.columnDef ? col.columnDef.accessorKey : col.id;
				const raw = get(row.original, columnKey);
				const formatFn = col.columnDef.meta?.exportFormat;

				return formatFn ? formatFn(raw, row.original) : raw;
			});
			allRows.push(parentData);

			if (row.getIsExpanded() && expandedRowPanelExport) {
				const expanded = expandedRowPanelExport(row);
				const childRows = Array.isArray(expanded) ? expanded : (expanded?.rows ?? []);
				const childHeaders = Array.isArray(expanded)
					? Object.keys(childRows[0] ?? {})
					: (expanded?.headers ?? []);

				if (childRows.length && childHeaders.length) {
					allRows.push(childHeaders);
					childRows.forEach(child => {
						const rowArray = childHeaders.map(key => {
							const val = child[key];
							return typeof val === "boolean" ? String(val) : (val ?? "");
						});
						allRows.push(rowArray);
					});
				}
			}
		});

		return allRows;
	};

	const getExportJson = (): Record<string, any>[] => {
		const rows = table.getRowModel().rows;

		return rows.map(row => {
			const obj: Record<string, any> = {};

			// Parent data
			visibleColumns.forEach(col => {
				const raw = get(row.original, col.id);
				const label = typeof col.columnDef.header === "string" ? col.columnDef.header : col.id;
				const formatFn = col.columnDef.meta?.exportFormat;
				obj[label] = formatFn ? formatFn(raw, row.original) : raw;
			});

			if (row.getIsExpanded() && expandedRowPanelExport) {
				const expanded = expandedRowPanelExport(row);
				const childRows = Array.isArray(expanded) ? expanded : (expanded?.rows ?? []);
				obj.children = childRows;
			}

			return obj;
		});
	};

	const exportNow = () => {
		const data = getExportData();
		if (data.length === 0) return;

		const allRows = getExportRows();
		const file = filename + (format === "csv" ? ".csv" : format === "excel" ? ".xlsx" : ".json");

		if (format === "csv") exportToCSV(allRows, headers, file);
		else if (format === "excel") exportToExcel(allRows, headers, file);
		else exportToJSON(getExportJson(), file);
	};

	const exportData = getExportData();
	const rowCount = exportData.length;
	return {
		format,
		exportAll,
		setFormat,
		setExportAll,
		exportNow,
		rowCount,
		possibleExportAllData: allData,
	};
};
